<template>
    <template v-for="(seat, index) in computedSeats" :key="`seat-reservation-number-${index}`">
        <template v-if="seat">
            <Seat
                :type="getSeatType(seat.status)"
                :value="seat.seat_number"
                @click="seat.status === SeatStatus.Available && selectSeat(seat.seat_number)"
            />

            <Seat v-if="((index + 1) === 2 || ((index + 1) + 2) % 4 === 0)" :type="SeatType.Placeholder" />
        </template>

        <template v-else>
            <Seat :type="SeatType.Placeholder" />
        </template>
    </template>

    <template v-for="(seat) in reorderedSeats" :key="`seat-reservation-number-${seat?.seat_number}`">
        <Seat
            :type="getSeatType(seat.status)"
            :value="seat.seat_number"
            @click="seat.status === SeatStatus.Available && selectSeat(seat.seat_number)"
        />
    </template>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import Seat from './Seat.vue';
import { SeatStatus, SeatType } from '../../types';
import { useSeatReservation } from '../../modules/useSeatReservation';
import { CheckSeatsAvailabilityResponseItem } from '../../requestTypes';

interface Props {
    numberOfSeats: number;
    singlePlaceholderBefore?: number[];
    doublePlaceholderBefore?: number[];
    reorderSeats?: number[]
}

const props = withDefaults(defineProps<Props>(), {
    singlePlaceholderBefore: () => [],
    doublePlaceholderBefore: () => [],
});

const { seatReservationState, selectSeat } = useSeatReservation();

const computedSeats = computed(() => {
    const output: (CheckSeatsAvailabilityResponseItem | null)[] = [];

    seatReservationState.allSeats.forEach((item) => {
        if (item.seat_number > props.numberOfSeats) {
            return;
        }

        if (props.doublePlaceholderBefore.includes(item.seat_number)) {
            output.push(null);
            output.push(null);
        }

        if (props.singlePlaceholderBefore.includes(item.seat_number)) {
            output.push(null);
        }

        if (!props.reorderSeats || !props.reorderSeats?.includes(item.seat_number)) {
            output.push(item);
        }
    });

    return output;
});

const reorderedSeats = computed(() => {
    if (!props.reorderSeats) {
        return [];
    }

    return seatReservationState.allSeats.filter((item) => props.reorderSeats?.includes(item.seat_number)).sort((a, b) => {
        if (!props.reorderSeats) {
            return 0;
        }

        const indexA = props.reorderSeats.findIndex((ri: number) => a.seat_number === ri);
        const indexB = props.reorderSeats.findIndex((ri: number) => b.seat_number === ri);

        return indexA - indexB;
    });
});

const getSeatType = (seatStatus: SeatStatus) => {
    switch (seatStatus) {
        case SeatStatus.Available:
            return SeatType.Available;
        case SeatStatus.Selected:
            return SeatType.Selected;
        case SeatStatus.Blocked:
            return SeatType.Occupied;
        default:
            return SeatType.Occupied;
    }
};

</script>
